import { useEffect, useState, useCallback } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminCtx, btnPrimary } from "@/components/admin/ui";
import { AdminLayout } from "@/components/admin/AdminLayout";

export default function AdminGuard() {
  const navigate = useNavigate();
  const { ready, isAuthed } = useAuth();
  const [state, setState] = useState("checking"); // checking | ok | denied
  const [admin, setAdmin] = useState(null);

  const load = useCallback(async () => {
    try {
      const me = await api.adminMe();
      setAdmin(me);
      setState("ok");
    } catch (e) {
      if (e?.response?.status === 401) { navigate("/admin/login", { replace: true }); return; }
      setState("denied");
    }
  }, [navigate]);

  useEffect(() => {
    if (!ready) return;
    if (!isAuthed) { navigate("/admin/login", { replace: true }); return; }
    load();
  }, [ready, isAuthed, load, navigate]);

  if (state === "checking" || !ready) {
    return <div className="min-h-screen grid place-items-center bg-[#F9F8F6]"><Loader2 className="animate-spin text-[#1F2A22]" /></div>;
  }
  if (state === "denied") {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F9F8F6] px-6" data-testid="admin-denied">
        <div className="text-center max-w-sm">
          <ShieldAlert size={44} className="mx-auto text-[#D9381E]" />
          <h1 className="text-2xl font-serif text-[#1F2A22] mt-4">Restricted area</h1>
          <p className="text-[#5C6B61] mt-2 text-sm">This account does not have HomeRange admin access.</p>
          <button onClick={() => navigate("/")} className={`${btnPrimary} mt-6`} data-testid="admin-denied-home">Return to HomeRange</button>
        </div>
      </div>
    );
  }
  return (
    <AdminCtx.Provider value={{ admin, reloadAdmin: load, can: (p) => admin?.permissions?.includes(p) }}>
      <AdminLayout>
        <Outlet />
      </AdminLayout>
    </AdminCtx.Provider>
  );
}
